import { useEffect, useMemo, useState } from "react";
import Button from "../../ui/buttons/Button";
import DropdownSelectPill from "../../ui/inputs/DropdownSelectPill";
import FloatingModal from "../../ui/Modals/FloatingModal";
import rocket from "../../assets/icons/rocket.svg";
import rocketHover from "../../assets/icons/rocket-hover.svg";
import { templatesService } from "../../services/templates.service";
import { leadsService } from "../../services/leads.service";
import styles from "../../styles/components/leads/BatchCommunicationModal.module.css";

const DEFAULT_REMINDER_DAYS = 7;

function extractBackendError(errorData) {
  if (!errorData) return "No se pudieron crear las comunicaciones.";
  if (typeof errorData === "string") return errorData;
  return (
    errorData.leads?.[0] ||
    errorData.template_group?.[0] ||
    errorData.reminder_days?.[0] ||
    errorData.detail ||
    "No se pudieron crear las comunicaciones."
  );
}

function BatchCommunicationModal({
  count,
  leadIds,
  accountId,
  accountName,
  mailAccounts = [],
  onClose,
}) {
  const [templateGroups, setTemplateGroups] = useState([]);
  const [templateGroupId, setTemplateGroupId] = useState(null);
  const [reminderDays, setReminderDays] = useState(DEFAULT_REMINDER_DAYS);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [templateGroupsLoadFailed, setTemplateGroupsLoadFailed] =
    useState(false);
  const [templateGroupsLoadAttempt, setTemplateGroupsLoadAttempt] = useState(0);
  const [result, setResult] = useState(null);
  const [sendHovered, setSendHovered] = useState(false);
  const [showMailList, setShowMailList] = useState(false);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const data = await templatesService.fetchTemplateGroups();
        const results = Array.isArray(data) ? data : (data?.results ?? []);
        const filtered = results.filter(
          (group) => group.account?.id === accountId,
        );
        if (active) {
          setTemplateGroups(filtered);
          setTemplateGroupsLoadFailed(false);
        }
      } catch (error) {
        if (active) {
          setTemplateGroupsLoadFailed(true);
          setErrorMessage("No se pudieron cargar los Template Groups.");
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [accountId, templateGroupsLoadAttempt]);

  const templateOptions = useMemo(
    () =>
      templateGroups.map((group) => ({
        value: group.id,
        label: group.group?.name ?? group.name ?? `Grupo ${group.id}`,
      })),
    [templateGroups],
  );

  const mailsCount = count ?? leadIds.length;
  const disableSend = !templateGroupId || submitting;
  const primaryMailAccount = mailAccounts[0] ?? null;
  const extraMailAccounts = mailAccounts.slice(1);

  const handleSubmit = async () => {
    setErrorMessage("");
    setSubmitting(true);

    try {
      const data = await leadsService.createBatchMails({
        leads: leadIds,
        templateGroupId,
        reminderDays,
      });
      setResult(data);
    } catch (error) {
      setErrorMessage(extractBackendError(error?.response?.data));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <FloatingModal bottom={56} direction="column" gap={16}>
        <span className={styles.resultMessage}>
          Comunicaciones creadas: {result.created} nuevas y {result.updated}{" "}
          actualizadas (de {result.total}).
        </span>
        <Button
          tone="green"
          height={40}
          className={styles.okButton}
          onClick={onClose}
        >
          Ok
        </Button>
      </FloatingModal>
    );
  }

  if (errorMessage) {
    const handleErrorDismiss = () => {
      setErrorMessage("");
      if (templateGroupsLoadFailed) {
        setTemplateGroupsLoadAttempt((previousAttempt) => previousAttempt + 1);
      }
    };

    return (
      <FloatingModal
        bottom={56}
        direction="column"
        gap={16}
        onClickOutside={onClose}
      >
        <span className={styles.errorMessage}>{errorMessage}</span>
        <Button
          variant="secondary"
          tone="blue"
          height={44}
          onClick={handleErrorDismiss}
        >
          {templateGroupsLoadFailed ? "Reintentar" : "Volver"}
        </Button>
      </FloatingModal>
    );
  }

  return (
    <FloatingModal bottom={56} gap={24}>
      <div className={styles.infoColumn}>
        <span className={styles.sendSummaryLine}>
          <span className={styles.nowrap}>
            You will send {mailsCount} mails from
          </span>
          <span title={accountName} className={styles.accountPill}>
            {accountName}
          </span>
        </span>

        {primaryMailAccount ? (
          <span className={styles.mailAccountsLine}>
            <span className={styles.nowrap}>from</span>
            <span
              title={primaryMailAccount.email}
              className={styles.primaryMailAddress}
            >
              {primaryMailAccount.email}
            </span>

            {extraMailAccounts.length > 0 ? (
              <span
                className={styles.extraMailsWrapper}
                onMouseEnter={() => setShowMailList(true)}
                onMouseLeave={() => setShowMailList(false)}
              >
                <span className={styles.extraMailsBadge}>
                  +{extraMailAccounts.length}
                </span>

                {showMailList ? (
                  <div className={styles.mailListPopover}>
                    {mailAccounts.map((account) => (
                      <span key={account.id} className={styles.mailListItem}>
                        {account.email}
                      </span>
                    ))}
                  </div>
                ) : null}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>

      <DropdownSelectPill
        options={templateOptions}
        placeholder="Template Group"
        value={templateGroupId}
        onChange={(selectedValue) => setTemplateGroupId(selectedValue)}
        chevronSize={16}
        dropUp
        menuMaxHeight={320}
        menuMinWidth={200}
        height={50}
        style={{
          backgroundColor: "var(--surface-input-hover)",
          color: "var(--text-primary)",
        }}
      />

      <div className={styles.reminderControl}>
        <button
          type="button"
          disabled={reminderDays <= 0}
          onClick={() => setReminderDays((prev) => Math.max(0, prev - 1))}
          className={styles.spinnerButton}
        >
          -
        </button>

        <span className={styles.reminderLabel}>
          {reminderDays} Días de Reminder
        </span>

        <button
          type="button"
          onClick={() => setReminderDays((prev) => prev + 1)}
          className={styles.spinnerButton}
        >
          +
        </button>
      </div>

      <Button
        tone="green"
        iconRight={
          <img
            src={sendHovered ? rocketHover : rocket}
            alt=""
            className={styles.sendIcon}
          />
        }
        height={50}
        className={styles.sendButton}
        disabled={disableSend}
        loading={submitting}
        onClick={handleSubmit}
        onMouseEnter={() => setSendHovered(true)}
        onMouseLeave={() => setSendHovered(false)}
      >
        Enviar mails a revisión
      </Button>
    </FloatingModal>
  );
}

export default BatchCommunicationModal;
